import { FormEvent, useState } from "react";
import { useMe } from "../../context/MeContext";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import PositiveButton from "../PositiveButton";
import { Title, NewTitle } from "../../types/Title";
import useTitlesService from "../../services/TitlesService";
import useUrlUtilities from "../../utilities/UrlUtilities";

interface NewTitleFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSaved?: (experience: Title) => void;
  onCancel?: () => void;
}

export default function NewTitleForm({
  onSaved,
  open,
  setOpen,
  onCancel
}: NewTitleFormProps) {
  const { me } = useMe();

  const { isValidUrl } = useUrlUtilities();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    url: ""
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const { createTitle } = useTitlesService();

  const validateForm = () => {
    let isValid = true;
    const newErrors: any = {};

    if (formData.name.trim() === "") {
      newErrors.name = "Experience name is required";
      isValid = false;
    }

    if ((formData.url ?? "").trim() !== "") {
      // Check the website url is a valid url using regex.
      if (!isValidUrl(formData.url!.trim())) {
        newErrors.url =
          "Url is not a valid url in the form https://company.com";
        isValid = false;
      }
    }

    setValidationErrors(newErrors);

    return isValid;
  };

  const saveTitle = async (e: FormEvent) => {
    e.preventDefault();

    setSubmitted(true);

    if (!validateForm()) return;

    setIsSaving(true);

    try {

        const newTitleDetails: NewTitle = {
          name: formData.name,
        description: formData.description,
        url: formData.url
        };

      const newTitle = await createTitle(newTitleDetails);

      if (onSaved) {
        onSaved(newTitle);
      }

      setFormData({
        name: "",
        url: "",
        description: ""
      });

      if (!onSaved) {
        setOpen(false);
      }
      
    } catch (error) {
      const newErrors: any = {
        name: "Failed to save title. Please try again. If the problem persists, please contact support.",
      };
      setValidationErrors(newErrors);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validStyle = (fieldName: string) => {
    // Find the validationErrors key with the fieldName.
    let valid = Object.keys(validationErrors).indexOf(fieldName) === -1;

    return valid ? "" : "border-red-800";
  };

  const validationError = (fieldName: string): null | string => {
    return validationErrors[fieldName] || null;
  };

  const isFormValid = submitted && Object.keys(validationErrors).length === 0;

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-slate-900 text-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <DialogTitle
              as="h3"
              className="text-base leading-6 text-white pb-4"
            >
              Create a new title
            </DialogTitle>
            <form
              onSubmit={saveTitle}
              className="card p-8 flex flex-col form grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-4"
            >
              <div className="sm:col-span-4">
                <label htmlFor="name" className="block leading-6">
                  Title name:
                </label>
                <div className="mt-2">
                  <input
                    disabled={isSaving}
                    type="text"
                    name="name"
                    placeholder="Title name *"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                      "name"
                    )}`}
                  />
                </div>
                {validationError("name") && (
                  <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                    {validationError("name")}
                  </div>
                )}
              </div>
              <div className="sm:col-span-4">
                <label htmlFor="url" className="block leading-6">
                  Url:
                </label>
                <div className="mt-2">
                  <input
                    disabled={isSaving}
                    type="url"
                    name="url"
                    placeholder="Official URL"
                    value={formData.url}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                      "url"
                    )}`}
                  />
                </div>
                {validationError("url") && (
                  <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                    {validationError("url")}
                  </div>
                )}
              </div>
              {/* Submit Button */}
              <div className="sm:col-span-4 flex items-center gap-2 justify-end">
                <PositiveButton
                  type="submit"
                  disabled={isSaving}
                >
                  {isSaving ? "Creating..." : "Create title"}
                </PositiveButton>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
